import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { useFacilityData } from '../../../../hooks/useFacilityData';
import { extractFacilityTitleAndSubtitle } from '../../../../utils/facilityUtils';

// Icon mapping for facility types
const FACILITY_ICONS = {
  hospital: { icon: '🏥', color: '#4B9BFF', bgColor: '#E3F2FF' },
  clinic: { icon: '🏥', color: '#10B981', bgColor: '#D1FAE5' },
  pharmacy: { icon: '💊', color: '#F59E0B', bgColor: '#FEF3C7' },
  lab: { icon: '🔬', color: '#8B5CF6', bgColor: '#EDE9FE' },
  nursing: { icon: '👩‍⚕️', color: '#EC4899', bgColor: '#FCE7F3' },
  rehab: { icon: '♿', color: '#06B6D4', bgColor: '#E0F2FE' },
  default: { icon: '🏢', color: webColors.primary, bgColor: webColors.primaryLight },
};

const getFacilityIcon = (facilityName) => {
  const lowerName = facilityName.toLowerCase();
  if (lowerName.includes('hospital')) return FACILITY_ICONS.hospital;
  if (lowerName.includes('clinic')) return FACILITY_ICONS.clinic;
  if (lowerName.includes('pharmacy')) return FACILITY_ICONS.pharmacy;
  if (lowerName.includes('lab')) return FACILITY_ICONS.lab;
  if (lowerName.includes('nursing')) return FACILITY_ICONS.nursing;
  if (lowerName.includes('rehab')) return FACILITY_ICONS.rehab;
  return FACILITY_ICONS.default;
};

const StepFacility = ({ formData, updateFormData, goToStep }) => {
  const [expandedTypes, setExpandedTypes] = useState([]);
  const [searchText, setSearchText] = useState('');

  // Fetch facility types using the existing hook
  const { data: facilities, isLoading, error, refetch, isUsingNewSystem } = useFacilityData(formData.country);

  const toggleExpanded = (facilityId) => {
    setExpandedTypes(prev => {
      if (prev.includes(facilityId)) {
        return prev.filter(id => id !== facilityId);
      }
      return [...prev, facilityId];
    });
  };

  const selectFacility = (parentType, subtype) => {
    updateFormData('facilityType', parentType.id);
    updateFormData('facilityTypeName', parentType.name);
    updateFormData('facilitySubtype', subtype.id);
    updateFormData('facilitySubtypeName', subtype.name);

    // Store fac_id if using new system
    if (isUsingNewSystem && subtype.fac_id) {
      updateFormData('fac_id', subtype.fac_id);
    }
  };

  const selectParentFacility = (facility) => {
    // For facilities without subtypes, select the parent directly
    updateFormData('facilityType', facility.id);
    updateFormData('facilityTypeName', facility.name);
    updateFormData('facilitySubtype', facility.id);
    updateFormData('facilitySubtypeName', facility.name);

    if (isUsingNewSystem && facility.fac_id) {
      updateFormData('fac_id', facility.fac_id);
    }
  };

  const filteredFacilities = useMemo(() => {
    if (!facilities) return [];
    if (!searchText) return facilities;

    const lowerSearch = searchText.toLowerCase();
    return facilities.filter((facility) => {
      const matchesName = facility.name.toLowerCase().includes(lowerSearch);
      const hasMatchingType = facility.types?.some(type =>
        type.name.toLowerCase().includes(lowerSearch)
      );
      return matchesName || hasMatchingType;
    });
  }, [facilities, searchText]);

  const isSelected = formData.facilitySubtype !== null;

  // Loading state
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={webColors.primary} />
        <Text style={styles.loadingText}>Loading facility types...</Text>
      </View>
    );
  }

  // Error state
  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Unable to load facility types. Please check your internet connection.</Text>
        <TouchableOpacity style={styles.retryBtn} onPress={() => refetch()}>
          <Text style={styles.retryBtnText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepIcon}>🏢</Text>
        <Text style={styles.stepTitle}>Select Facility Type</Text>
        <Text style={styles.stepSubtitle}>Choose your medical facility type</Text>
      </View>

      {/* Search Input */}
      <View style={styles.searchWrapper}>
        <Text style={styles.searchIcon}>🔍</Text>
        <TextInput
          style={styles.searchInput}
          placeholder="Search facility type..."
          placeholderTextColor={webColors.textMuted}
          value={searchText}
          onChangeText={(text) => {
            setSearchText(text);
            // Auto-expand when searching
            if (text.length > 0) {
              setExpandedTypes(filteredFacilities.map(f => f.id));
            } else {
              setExpandedTypes([]);
            }
          }}
        />
      </View>

      {/* Facility List */}
      <ScrollView style={styles.facilityList} nestedScrollEnabled>
        {filteredFacilities.map((facility) => {
          const isExpanded = expandedTypes.includes(facility.id) || searchText.length > 0;
          const iconConfig = getFacilityIcon(facility.name);
          const hasSubtypes = facility.types && facility.types.length > 0;
          const { title, subtitle } = extractFacilityTitleAndSubtitle(facility.name);

          return (
            <View key={facility.id} style={styles.facilityItem}>
              {/* Parent Facility */}
              <TouchableOpacity
                style={[
                  styles.facilityCard,
                  isExpanded && styles.facilityCardExpanded,
                  !hasSubtypes && formData.facilitySubtype === facility.id && styles.facilityCardSelected,
                ]}
                onPress={() => {
                  if (hasSubtypes) {
                    toggleExpanded(facility.id);
                  } else {
                    selectParentFacility(facility);
                  }
                }}
              >
                <View style={[styles.facilityIconWrapper, { backgroundColor: iconConfig.bgColor }]}>
                  <Text style={styles.facilityIcon}>{iconConfig.icon}</Text>
                </View>
                <View style={styles.facilityInfo}>
                  <Text style={[
                    styles.facilityName,
                    isExpanded && { color: iconConfig.color },
                  ]}>
                    {title}
                  </Text>
                  {subtitle && (
                    <Text style={styles.facilitySubtitle}>{subtitle}</Text>
                  )}
                  {hasSubtypes && (
                    <Text style={styles.facilityCount}>
                      {facility.types.length} type{facility.types.length !== 1 ? 's' : ''}
                    </Text>
                  )}
                </View>
                {hasSubtypes ? (
                  <Text style={[styles.expandArrow, isExpanded && styles.expandArrowRotated]}>
                    ▶
                  </Text>
                ) : (
                  formData.facilitySubtype === facility.id && (
                    <View style={[styles.checkCircle, { backgroundColor: iconConfig.color }]}>
                      <Text style={styles.checkMark}>✓</Text>
                    </View>
                  )
                )}
              </TouchableOpacity>

              {/* Subtypes */}
              {hasSubtypes && isExpanded && (
                <View style={styles.subtypesList}>
                  {facility.types.map((subtype) => {
                    const isSubtypeSelected = formData.facilitySubtype === subtype.id;
                    const { title: subtypeTitle, subtitle: subtypeSubtitle } = extractFacilityTitleAndSubtitle(subtype.name);

                    return (
                      <TouchableOpacity
                        key={subtype.id}
                        style={[
                          styles.subtypeCard,
                          isSubtypeSelected && styles.subtypeCardSelected,
                        ]}
                        onPress={() => selectFacility(facility, subtype)}
                      >
                        <View style={[
                          styles.radioCircle,
                          isSubtypeSelected && { backgroundColor: iconConfig.color, borderColor: iconConfig.color },
                        ]}>
                          {isSubtypeSelected && <Text style={styles.radioCheck}>✓</Text>}
                        </View>
                        <View style={styles.subtypeInfo}>
                          <Text style={[
                            styles.subtypeName,
                            isSubtypeSelected && { color: iconConfig.color },
                          ]}>
                            {subtypeTitle}
                          </Text>
                          {subtypeSubtitle && (
                            <Text style={styles.subtypeSubtitle}>{subtypeSubtitle}</Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>

      <View style={styles.btnRow}>
        <TouchableOpacity
          style={styles.btnBack}
          onPress={() => goToStep(1)}
        >
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnNext, !isSelected && styles.btnNextDisabled]}
          onPress={() => goToStep(3)}
          disabled={!isSelected}
        >
          <Text style={[styles.btnNextText, !isSelected && styles.btnNextTextDisabled]}>
            Next
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: webColors.textMuted,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    fontSize: 16,
    color: webColors.textMuted,
    textAlign: 'center',
    marginBottom: 16,
  },
  retryBtn: {
    backgroundColor: webColors.primary,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: webBorderRadius.medium,
  },
  retryBtnText: {
    color: '#ffffff',
    fontWeight: '600',
  },
  stepHeader: {
    marginBottom: 24,
  },
  stepIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: webColors.primary,
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  searchIcon: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: [{ translateY: -9 }],
    fontSize: 18,
    zIndex: 1,
  },
  searchInput: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingLeft: 48,
    paddingRight: 16,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  facilityList: {
    maxHeight: 400,
    marginBottom: 24,
  },
  facilityItem: {
    marginBottom: 12,
  },
  facilityCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    ...webShadows.card,
  },
  facilityCardExpanded: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  facilityCardSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  facilityIconWrapper: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  facilityIcon: {
    fontSize: 24,
  },
  facilityInfo: {
    flex: 1,
  },
  facilityName: {
    fontSize: 17,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  facilitySubtitle: {
    fontSize: 14,
    color: webColors.textMuted,
    marginTop: 2,
  },
  facilityCount: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 4,
  },
  expandArrow: {
    fontSize: 12,
    color: webColors.textMuted,
  },
  expandArrowRotated: {
    transform: [{ rotate: '90deg' }],
    color: webColors.primary,
  },
  checkCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkMark: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  subtypesList: {
    marginTop: 8,
    marginLeft: 24,
  },
  subtypeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    marginBottom: 8,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
  },
  subtypeCardSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  radioCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: webColors.border,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  radioCheck: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '700',
  },
  subtypeInfo: {
    flex: 1,
  },
  subtypeName: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  subtypeSubtitle: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 2,
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  btnBack: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: '#374151',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    backgroundColor: webColors.border,
    shadowOpacity: 0,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNextTextDisabled: {
    color: webColors.textMuted,
  },
});

export default StepFacility;
